import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BellOff, MessageCircle, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import { useHub } from "@/components/AppShell";
import { Loader } from "@/components/common";
import { timeLabel } from "@/lib/connect";
import { ConvFace, PINK } from "@/components/connect/parts";
import Thread from "@/components/connect/Thread";
import NewChat from "@/components/connect/NewChat";

export default function Messages() {
  const { hub, reloadHub } = useHub();
  const { cid } = useParams();
  const nav = useNavigate();
  const base = `/organizations/${hub.id}/connect`;
  const [d, setD] = useState(null);
  const [people, setPeople] = useState(null);
  const load = useCallback(() => api.get(`${base}/conversations`).then(({ data }) => setD(data)), [base]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(() => { load(); reloadHub(); }, (m) => ["message", "conversation"].includes(m.entity), 8000);
  const changed = useCallback(() => { load(); reloadHub(); }, [load, reloadHub]);
  if (!d) return <Loader />;
  const open = (id) => { setPeople(null); nav(`/h/${hub.id}/connect/messages/${id}`); };
  return (
    <div className="-mx-4 -my-6 flex h-[calc(100dvh-64px)] overflow-hidden border-q-line sm:mx-0 sm:my-0 sm:h-[calc(100dvh-120px)] sm:rounded-2xl sm:border" data-testid="messages-page">
      <aside className={`${cid ? "hidden md:flex" : "flex"} w-full flex-col border-r border-q-line md:w-[340px] md:flex-shrink-0`}>
        <div className="flex items-center justify-between p-4">
          <h1 className="font-display text-2xl font-extrabold text-q-text" data-testid="messages-title">Messages</h1>
          {!hub.read_only && <button className="q-btn q-btn-primary" onClick={() => api.get(`${base}/people`).then(({ data }) => setPeople(data))} data-testid="messages-new-btn"><Plus className="h-4 w-4" /> New</button>}
        </div>
        <ul className="flex-1 overflow-y-auto px-2 pb-4" data-testid="conversation-list">
          {d.conversations.map((c) => (
            <li key={c.id}>
              <button onClick={() => open(c.id)} data-testid={`conversation-${c.kind === "family" ? "family" : c.id}`}
                className={`flex min-h-[64px] w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors duration-150 hover:bg-q-raised ${cid === c.id ? "bg-q-raised" : ""}`}>
                <ConvFace c={c} me={d.me} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2">
                    <span className={`truncate ${c.unread ? "font-extrabold text-q-text" : "font-semibold text-q-text"}`} data-testid={`conversation-name-${c.id}`}>{c.name}</span>
                    <span className="flex-shrink-0 text-[11px] text-q-faint">{timeLabel(c.last_at)}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="flex-1 truncate text-sm text-q-muted">{c.last_preview || "No messages yet"}</span>
                    {c.muted && <BellOff className="h-3.5 w-3.5 text-q-faint" />}
                    {!!c.unread && <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-black text-white" style={{ background: `rgb(${PINK})` }} data-testid={`conversation-unread-${c.id}`}>{c.unread}</span>}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </aside>
      {cid ? <Thread key={cid} hub={hub} base={base} convId={cid} calls={d.calls} onBack={() => nav(`/h/${hub.id}/connect/messages`)} onChanged={changed} /> : (
        <div className="hidden flex-1 flex-col items-center justify-center gap-3 text-q-muted md:flex" data-testid="messages-empty-thread">
          <MessageCircle className="h-10 w-10" style={{ color: `rgb(${PINK})` }} /> Pick a conversation
        </div>
      )}
      {people && <NewChat base={base} people={people.people} canGroup={people.can_create_group} onClose={() => setPeople(null)} onOpen={open} />}
    </div>
  );
}
